import React, { useEffect, useState, useCallback } from 'react';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Hero from '../components/Hero';
import IncidentWatchStrip from '../components/IncidentWatchStrip';
import Stats from '../components/Stats';
import About from '../components/About';
import AIChatBot from '../components/AIChatBot';
import FeaturedSections from '../components/FeaturedSections';
import PostsMosaic from '../components/PostsMosaic';
import LatestGallery from '../components/LatestGallery';
// RecommendedSection removed from homepage — now lives on post/event pages only.
import PropertyOfTheWeek from '../components/PropertyOfTheWeek';
import WhatsOnThisWeekend from '../components/WhatsOnThisWeekend';
import UpcomingEventsStrip from '../components/UpcomingEventsStrip';
import PropertiesShowcase from '../components/PropertiesShowcase';
import WeatherBanner from '../components/WeatherBanner';
import CategoryTiles from '../components/CategoryTiles';
import JobsStrip from '../components/JobsStrip';
import LiveTravelStrip from '../components/LiveTravelStrip';
import LiveCameraStrip from '../components/LiveCameraStrip';
import StMirrenStrip from '../components/StMirrenStrip';
import NetworkBadge from '../components/NetworkBadge';
import FoodDrinkReviews from '../components/FoodDrinkReviews';
import TrendingInPaisley from '../components/TrendingInPaisley';
import LiveMusicNearby from '../components/LiveMusicNearby';
import BookAStayTile from '../components/BookAStayTile';
import PlacesToStayTile from '../components/PlacesToStayTile';
import FollowUsBanner from '../components/FollowUsBanner';
import SubscribeWidget from '../components/SubscribeWidget';
import AdvertisersCarousel from '../components/AdvertisersCarousel';
import Announcements from '../components/Announcements';
import SupportersCarousel from '../components/SupportersCarousel';
import FeaturedPartnerSpotlight from '../components/FeaturedPartnerSpotlight';
import Footer from '../components/Footer';
import { postsAPI, statsAPI, advertisersAPI, supportersAPI, featuredAPI } from '../api';

const HomePage = () => {
  const [data, setData] = useState({
    posts: [],
    stats: null,
    advertisers: [],
    supporters: [],
    featured: [],
    loading: true,
  });

  const loadData = useCallback(async () => {
    try {
      // Pull a larger sample so we can pick featured-first across multiple
      // categories (excluding announcements which the user wants hidden).
      const [news, history, events, attractions, stats, advertisers, supporters, featured] = await Promise.all([
        postsAPI.getAll('news', 12, true),
        postsAPI.getAll('history', 4, true),
        postsAPI.getAll('events', 4, true),
        postsAPI.getAll('attractions', 4, true),
        statsAPI.get(),
        advertisersAPI.getAll(),
        supportersAPI.getAll(),
        featuredAPI.getAll(),
      ]);

      // Merge & dedupe by id, dropping anything in announcements category.
      const seen = new Set();
      const mixed = [...news.data, ...events.data, ...history.data, ...attractions.data]
        .filter((p) => p && p.published && p.category !== 'announcements')
        .filter((p) => (seen.has(p.id) ? false : (seen.add(p.id), true)));

      setData({
        posts: mixed,
        stats: stats.data,
        advertisers: advertisers.data,
        supporters: supporters.data,
        featured: featured.data,
        loading: false,
      });
    } catch (error) {
      console.error('Error loading homepage data:', error);
      setData((prev) => ({ ...prev, loading: false }));
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <>
      <SEO
        title="Paisley.org.uk | News, Events & Community Guide to Paisley"
        description="Paisley news, events, road updates, history, attractions and local businesses from across Paisley and Renfrewshire, Scotland."
        keywords="Paisley Scotland, Paisley news, Paisley events, Paisley community, Paisley business directory, Renfrewshire, visit Paisley"
        url="/"
        isHomepage
      />
      {/* SEO H1 — visually concealed so the visual Hero stays the same */}
      <h1 className="sr-only" data-testid="homepage-h1">Paisley News, Events &amp; Community Guide</h1>
      <Header />
      <Hero />
      <IncidentWatchStrip />
      <WeatherBanner />
      <LiveTravelStrip />
      <LiveCameraStrip />
      <StMirrenStrip />
      <Stats statsData={data.stats} />

      {/* Editorial mosaic — replaces old LatestNews */}
      <PostsMosaic posts={data.posts} />

      {/* Trending flywheel — most-viewed articles this month */}
      <TrendingInPaisley />

      <PropertyOfTheWeek />
      <WhatsOnThisWeekend />
      <LatestGallery />

      {/* Affiliate "Recommended" grid removed from homepage per Brian's
          request — moved to a single-item slot on post/event pages instead
          (see PostDetail.jsx + EventDetail.jsx). Admin catalogue lives at
          /admin/affiliate. */}

      {/* Discover Paisley — content editable from /admin/dashboard */}
      <About />

      {/* Upcoming events — pulls from new events collection */}
      <UpcomingEventsStrip />

      {/* Live music & events (Ticketmaster affiliate feed) — placed directly
          under local events so ticketed gigs sit alongside community events */}
      <LiveMusicNearby />

      {/* Properties to Let showcase — live listings + Paisley Murals callout */}
      <PropertiesShowcase />

      {/* "Book a stay in Paisley" — Expedia (EGAC) affiliate tile. Placed
          after PropertiesShowcase so the visitor journey reads: rent long-term
          → visit short-term. */}
      <BookAStayTile />

      {/* "Places to stay" — 4 handpicked Booking.com hotel cards. The CJ
          Deep Link Automation script auto-monetises every outbound. */}
      <PlacesToStayTile />

      {/* Local Jobs — live mirror of paisleyscotland.com's jobs feed (no cache) */}
      <JobsStrip />

      {/* Quick category browse */}
      <CategoryTiles />

      <FeaturedSections featured={data.featured} />
      <AdvertisersCarousel advertisers={data.advertisers} />
      <SupportersCarousel supporters={data.supporters} />
      <FeaturedPartnerSpotlight />
      <Announcements />

      {/* Email subscribe — weekly digest */}
      <SubscribeWidget />

      {/* Network badge — paisley.org.uk / paisleyscotland.com / paisleybusiness.com */}
      <NetworkBadge />

      {/* Food & Drink Reviews — placed directly under the network badge */}
      <FoodDrinkReviews />

      {/* Big "Follow us on socials" tile — final conversion push before footer */}
      <FollowUsBanner />

      <Footer />
      <AIChatBot />
    </>
  );
};

export default HomePage;
